import { Button, Col, Grid, Typography } from 'gu-ui-kit'
import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Connected from 'modules/Services/NetworksList/subcomponents/Items/Connected'
import Unlinked from 'modules/Services/NetworksList/subcomponents/Items/Unlinked'
import ServicesListSkeleton from './subcomponents/Placeholder'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Services/NetworksList/filters'
import FilterPanel from 'components/Filters/FilterPanel'
import StoriesPreview from 'modules/Stories'
import styles from 'modules/Services/NetworksList/services-list.module.scss'
import { useCancelToken } from 'utils/hooks/useCancelToken'
import clsx from 'clsx'
import { PANEL_NAVIGATE } from 'modules/Tickets/types'
import { FETCH_ADVERTISING_TYPES, FETCH_NETWORKS_DATA, SET_FILTERS } from 'modules/Services/types'

const NetworksList = () => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()
  const dispatch = useDispatch()
  const { cancelableDispatch } = useCancelToken()
  const { loading, networks } = useSelector((state) => state['services/list'])
  const { data: storiesData } = useSelector(({ stories }) => stories)

  const { i18n, filters } = useSelector((state) => ({
    ...state['services/list'],
    ...state.app,
  }))

  const filterManager = useFiltersManager({
    filters: filters,
    config: config(),
    additionalFilterState: {
      ...filters,
    },
    setAction: SET_FILTERS,
    localStorageKey: 'networks-list',
  })

  const notHidden = useMemo(() => networks.filter((network) => !network.is_hidden), [networks])

  const networksConnected = useMemo(
    () =>
      notHidden
        .filter((network) => network.has_account)
        .sort((a, b) => {
          if (a > b) {
            return 1
          }

          if (a < b) {
            return -1
          }

          return 0
        }),
    [notHidden],
  )

  const networksDisconnected = useMemo(
    () =>
      notHidden
        .filter((network) => !network.has_account)
        .sort((a, b) => {
          if (a > b) {
            return 1
          }

          if (a < b) {
            return -1
          }

          return 0
        }),
    [notHidden],
  )

  useEffect(() => {
    new Promise((resolve, reject) =>
      cancelableDispatch({
        type: FETCH_ADVERTISING_TYPES,
        payload: { params: { lang: i18n }, meta: { resolve, reject } },
      }),
    ).then(({ data }) =>
      filterManager.updateConfiguration({
        key: 'advertising_type_ids',
        val: {
          options: data.map(({ name, id }) => ({
            value: id,
            label: name,
          })),
        },
      }),
    )
  }, [])

  useEffect(() => {
    if (!filterManager.filters) {
      return
    }

    const { query = '', ...filters } = filterManager.filters
    const queryParam = {}

    if (query?.length > 0) {
      queryParam.query = query
    }

    dispatch({
      type: FETCH_NETWORKS_DATA,
      payload: { params: { ...filters, ...queryParam } },
    })
  }, [filterManager.filters])

  return (
    <>
      <Helmet defer={false}>
        <title>{t('common::nav_menu.advertisement_networks')}</title>
      </Helmet>

      <Grid direction={'column'} gap={32}>
        <Grid
          wrap={breakpoint.fbs}
          justifyContent={breakpoint.fbs ? 'start' : 'between'}
          gap={32}
          direction={breakpoint.fb && storiesData.length > 0 ? 'column' : 'row'}
        >
          {breakpoint.fb && (
            <Grid fluid={false} className={styles['head__right-panel']}>
              <StoriesPreview className={clsx(styles['list__stories'])} />
            </Grid>
          )}
          <Grid fluid={false} className={styles['head__left-panel']} direction={'column'}>
            {filterManager.filters && (
              <FilterPanel
                Header={
                  <Typography
                    noMargin
                    className={breakpoint.sm ? 'margin-bottom-32' : 'margin-bottom-8'}
                    color={'Label-Primary'}
                    component={'h1'}
                    variant={breakpoint.fbs ? 'Header-40' : 'Header-60'}
                  >
                    {t('services::heading')}
                  </Typography>
                }
                filterManager={filterManager}
                className={styles['filter-panel']}
                ControlWrapper={({ children }) => (
                  <div className={styles['filter-panel__section-wrapper']}>{children}</div>
                )}
                AdditionalComponents={
                  !breakpoint.fb ? (
                    <Grid
                      fluid={false}
                      className={clsx(
                        styles['head__right-panel'],
                        styles['head__right-panel_in-panel'],
                      )}
                    >
                      <StoriesPreview className={clsx(styles['list__stories'])} />
                    </Grid>
                  ) : null
                }
              />
            )}
          </Grid>
        </Grid>
        {loading ? (
          <ServicesListSkeleton />
        ) : (
          <>
            <Grid gap={16} className={styles['list__container']}>
              {networksConnected.map((network, i) => (
                <Col
                  sm="6"
                  s="12"
                  md="4"
                  xl="3"
                  className={styles['list__col']}
                  key={'connected-' + i}
                >
                  <Connected {...network} key={'connected-' + network.type} />
                </Col>
              ))}
              {networksDisconnected.map((network, i) => (
                <Col
                  sm="6"
                  s="12"
                  md="4"
                  xl="3"
                  className={styles['list__col']}
                  key={'unlinked-' + i}
                >
                  <Unlinked {...network} key={'unlinked-' + network.type} />
                </Col>
              ))}
            </Grid>
            <Grid justifyContent={'center'}>
              <Button
                variant={'ghost'}
                onClick={() =>
                  dispatch({
                    type: PANEL_NAVIGATE,
                    payload: {
                      route: 'new_ticket',
                      params: { id: undefined, title: t('services::add_new') },
                    },
                  })
                }
              >
                {t('services::add_new')}
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </>
  )
}

export default NetworksList
